/**
 * Class that provides utilities for handling B2B differences between NM versions.
 * Note : the REACT HMIs were initially developed with NM Release R24.
 *
 * Hence releases before R24 are not supported.
 *
 */
import {
    DatasetDto,
    DatasetType,
    EHelpDeskAddFlightInFmpStamReroutingDto,
    EHelpDeskExcludeReIncludeFlightInRegulationDto,
    EHelpDeskForceFlightInRegulationDto,
    EHelpDeskTicketChoiceDto,
    EHelpDeskTicketRequestDetailsDto,
    EHelpDeskUnforceFlightInRegulationDto,
    FlightDto,
    FlightId,
    HotspotKind,
    HotspotSeverity,
    HotspotStatus,
    NmVersionId,
    UpdateFlightInMeasureDto,
} from 'app/model/CORE';
import MeasureUtils from 'app/common/util/MeasureUtils';
import AppContext from '../context/AppContext';

export default class B2BLegacyUtils {
    private static readonly NON_SOLUTION_38_EXCLUDED_HOTSPOT_SEVERITY: HotspotSeverity[] = [
        HotspotSeverity.NONE_SEVERITY,
    ];
    private static readonly NON_SOLUTION_38_EXCLUDED_HOTSPOT_STATUS: HotspotStatus[] = [HotspotStatus.CANCELLED];
    private static readonly NON_SOLUTION_38_EXCLUDED_HOTSPOT_KIND: HotspotKind[] = [HotspotKind.PROTECTION];

    /**
     * return true if version >= refVersion
     * @param version
     * @param refVersion
     */
    static isVersionHigherOrEqualThan(version: NmVersionId, refVersion: NmVersionId) {
        return version >= refVersion; // behind the scenes, we rely on an eum string comparison where "R24" > "R23_5"
    }

    /**
     * Return true of TrafficCountsByMeasure requests are possible
     */
    static isExistsTrafficCountsByMeasure() {
        return AppContext.isVersionHigherOrEqualThan(NmVersionId.R26);
    }

    /**
     * Return true of Flight list entry/exit times are HH:MM:SS
     */
    static isFlightListEntryExitTimeWithSeconds() {
        return AppContext.isVersionHigherOrEqualThan(NmVersionId.R26);
    }
    /**
     * Return true if supports Simulation reset (and measure revert) functionality.
     */
    static isSupportSimulationReset() {
        return AppContext.isVersionHigherOrEqualThan(NmVersionId.R26);
    }

    /**
     * Return true if the NM platform suppprts MCDMOnly measures.
     * (They have been removed in R26+)
     */
    static isSupportsMcdmOnlyMeasures() {
        return !AppContext.isVersionHigherOrEqualThan(NmVersionId.R26);
    }
    /**
     * Does the platform support S38
     * FMP   : Protection H/S
     * AOWIR : AvoidViaHotspot & AvoidViaRegulation, RouteInfo with NIA (counts, hotspots)
     */
    static isSupportsSolution38() {
        return AppContext.isNmvpBaseline() && AppContext.isVersionHigherOrEqualThan(NmVersionId.R25);
    }

    static getExcludedHotspotSeverityValues() {
        return B2BLegacyUtils.isSupportsSolution38()
            ? undefined
            : B2BLegacyUtils.NON_SOLUTION_38_EXCLUDED_HOTSPOT_SEVERITY;
    }

    static getExcludedHotspotStatusValues() {
        return B2BLegacyUtils.isSupportsSolution38()
            ? undefined
            : B2BLegacyUtils.NON_SOLUTION_38_EXCLUDED_HOTSPOT_STATUS;
    }

    static getExcludedHotspotKindValues() {
        return B2BLegacyUtils.isSupportsSolution38() ? undefined : B2BLegacyUtils.NON_SOLUTION_38_EXCLUDED_HOTSPOT_KIND;
    }

    /**
     * As of R24, certain UpdateFlightInMeasure operations must be replaced by eHelpDesk equivalents.
     * However, there are conditions where the older UpdateFlightInMeasure operations may be used
     * <li> in simulations - in order to actually cause the update
     * <li> for SESAR functionality not ported to eHelpDesk (RRTing before R26, MCDMOnly)
     * @since R24
     * @param nmVersion
     * @param dataset
     * @param fim
     * @return
     */
    static isUseEHelpDeskServices(dataset: DatasetDto, fim: UpdateFlightInMeasureDto) {
        return B2BLegacyUtils.isEHelpDeskAvailable(dataset) && B2BLegacyUtils.hasEHelpDeskEquivalent(fim);
    }

    /**
     * The EHelpDesk services are only available in R24+ for the OPERATIONAL dataset.
     * @param dataset
     * @return
     */
    static isEHelpDeskAvailable(dataset: DatasetDto) {
        return AppContext.isVersionHigherOrEqualThan(NmVersionId.R24) && dataset.type === DatasetType.OPERATIONAL;
    }

    /**
     * Return true if the given UpdateFlightInMeasureDto instance has an eHelpDesk equivalent.
     * @since R24
     * @param fim
     * @return
     */
    static hasEHelpDeskEquivalent(fim: UpdateFlightInMeasureDto) {
        return (
            MeasureUtils.isExcludeFlightInRegulation(fim) ||
            MeasureUtils.isReincludeFlightInRegulation(fim) ||
            MeasureUtils.isForceFlightInRegulation(fim) ||
            MeasureUtils.isUnforceFlightInRegulation(fim) ||
            (MeasureUtils.isAddFlightToRerouting(fim) && AppContext.isVersionHigherOrEqualThan(NmVersionId.R26)) ||
            (MeasureUtils.isRemoveFlightFromRerouting(fim) && AppContext.isVersionHigherOrEqualThan(NmVersionId.R26))
        );
    }

    /**
     * Return true if the given ticket choice is available via the eHelpDesk.
     * @param dataset
     * @param choice
     */
    static isEHelpDeskAvailableForChoice(dataset: DatasetDto, choice: keyof EHelpDeskTicketChoiceDto) {
        if (!B2BLegacyUtils.isEHelpDeskAvailable(dataset)) return false;
        switch (choice) {
            case 'addFlightsInFmpStamRerouting':
            case 'removeFlightsFromFmpStamRerouting':
                return AppContext.isVersionHigherOrEqualThan(NmVersionId.R26);
            default:
                return AppContext.isVersionHigherOrEqualThan(NmVersionId.R24);
        }
    }

    /**
     * Convert a given UpdateFlightInMeasureDto into an equivalent EHelpDeskTicketChoiceDto.
     * If such a conversion is not possible then null will be returned.
     *
     * @param fim
     * @param flight extra data for this flight (required in the conversion process)
     * @return
     */
    static convertToEHelpDeskChoice(fim: UpdateFlightInMeasureDto, flight: FlightDto): EHelpDeskTicketChoiceDto {
        let reply: EHelpDeskTicketChoiceDto = null;
        const fid: FlightId = { flightKeys: fim.flightKeys };
        const requestDetails: EHelpDeskTicketRequestDetailsDto = {
            requestText: 'TEST',
            inResponseTo: {
                eobt: fid.flightKeys.eobt,
                ctot: flight.calculatedTakeOffTime,
                mpr: flight.mostPenalisingRegulation,
            },
        };

        if (MeasureUtils.isExcludeFlightInRegulation(fim)) {
            const ticket: EHelpDeskExcludeReIncludeFlightInRegulationDto = {
                flightId: fid,
                regulations: fim.regulationIds,
                requestDetails: requestDetails,
            };
            reply = { excludeFlightFromRegulation: ticket };
        } else if (MeasureUtils.isReincludeFlightInRegulation(fim)) {
            const ticket: EHelpDeskExcludeReIncludeFlightInRegulationDto = {
                flightId: fid,
                regulations: fim.regulationIds,
                requestDetails: requestDetails,
            };
            reply = { reIncludeFlightInRegulation: ticket };
        } else if (MeasureUtils.isForceFlightInRegulation(fim)) {
            const ticket: EHelpDeskForceFlightInRegulationDto = {
                flightId: fid,
                updateTopicId: undefined, // TODO : later on we may need to consider this (otherwise each request will lead to a new ticket)
                newCto: fim.newCto,
                newCtot: fim.newCtot,
                requestDetails: requestDetails,
            };
            reply = { forceFlightsInRegulation: { flights: [ticket], mostPenalisingRegulation: fim.regulationId } };
        } else if (MeasureUtils.isUnforceFlightInRegulation(fim)) {
            const ticket: EHelpDeskUnforceFlightInRegulationDto = {
                flightId: fid,
                updateTopicId: undefined, // TODO : later on we may need to consider this (otherwise each request will lead to a new ticket)
                requestDetails: requestDetails,
            };
            reply = {
                unforceFlightsInRegulation: { flights: [ticket] },
            };
        } else if (MeasureUtils.isAddFlightToRerouting(fim)) {
            const ticket: EHelpDeskAddFlightInFmpStamReroutingDto = {
                flightId: fid,
                updateTopicId: undefined, // // TODO : later on we may need to consider this (otherwise each request will lead to a new ticket)
                requestDetails: requestDetails,
            };
            reply = {
                addFlightsInFmpStamRerouting: { reroutingId: fim.reroutingId, flights: [ticket] },
            };
        } else if (MeasureUtils.isRemoveFlightFromRerouting(fim)) {
            reply = {
                removeFlightsFromFmpStamRerouting: { reroutingId: fim.reroutingId, flights: [fim.flightKeys] },
            };
        }
        return reply;
    }
}
